/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type OnInit, type SimpleChanges, Component, Injector, Input, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import {
    type Rest, ActionAbstract,
    FormControl, FormGroup, SelectComponent, UNPLACEHOLDER,
} from '@dm/core';
import { type DataTurboClient, type DataTurboShare } from 'app/common/types/mo';
import { type Subscription } from 'rxjs';

import { FileSystemEntryE } from '../../../file-system/create-file-system/create-file-system.component';
import { UserTypeE } from '../../data-turbo-share/data-turbo-share-str.service';
import { CreateStyleE } from '../../share.enum';
import { CreateDataTurboShareComponent } from '../create-data-turbo-share/create-data-turbo-share.component';
import { DataTurboShareService } from '../data-turbo-share.service';
import { type ErrorMessage, DataTurboShareStrService } from '../data-turbo-share-str.service';


export interface CreateShare {
    new: { share: DataTurboShare; permissions: DataTurboClient[]; permissionNumber: number };
    copyFrom: { share: DataTurboShare; permissions: DataTurboClient[]; permissionNumber: number };
}

@Component({
    selector: 'app-copy-from-data-turbo-share',
    templateUrl: './copy-from-data-turbo-share.component.html',
    styleUrls: ['./copy-from-data-turbo-share.component.scss'],
})

export class CopyFromDataTurboShareComponent extends ActionAbstract implements OnInit, OnChanges {
    @ViewChild('DataTurboshareSelect') shareSelect: SelectComponent;
    @Input() vstoreId: string;
    @Input() vstoreName: string;

    @Input() fsName: string;
    @Input() vm: FormGroup;
    // 开关
    @Input() shareSwitch = true;

    @Input() smartGuiDataTurbo?: DataTurboShare;

    @Input() entry: FileSystemEntryE;

    @Input() supportSmartGui = true;

    i18n = this.str.i18n;
    UNPLACEHOLDER = UNPLACEHOLDER;
    shareRest: Rest;
    errorMessage: ErrorMessage;

    CreateStyleE = CreateStyleE;
    // 开关状态, 默认true
    switch = true;

    createStyle = CreateStyleE.New;

    selectShareOpt = null;
    // 加载文件时初始化，避免重复计算
    private timestamp = this.str.getTimeStamp();

    isAuditLogFs = false;

    $getCount: null | Subscription = null;

    //  new模式下的默认权限
    defaultNewPermission: DataTurboClient = {
        ID: 'Everyone-0',
        userName: '@Everyone',
        userType: UserTypeE.Everyone,
        permission: 1,
    };

    permissionsNumStr = '';

    // 待创建的共享数据，new和copyFrom分开，是为了记录数据。切换new和copyfrom的时候，数据不丢失。
    createShare: CreateShare = {
        new: {
            share: {
                name: '',
                sharePath: UNPLACEHOLDER,
                description: '',
            },
            permissions: null,
            permissionNumber: 0,
        },
        copyFrom: {
            share: {
                name: '',
                sharePath: UNPLACEHOLDER,
                description: '',
            },
            permissions: null,
            permissionNumber: 0,
        },
    };

    shareNameIsModified = false;

    copyFromSelection = null;

    constructor(
        injector: Injector,
        private str: DataTurboShareStrService,
        private service: DataTurboShareService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.isAuditLogFs = this.entry === FileSystemEntryE.AuditLog;
        if (this.smartGuiDataTurbo && this.smartGuiDataTurbo.name) {
            // 如果被推荐了共享名，那就不再变化，除非用户手动修改
            this.shareNameIsModified = true;
            this.createShare.new.share.name = this.smartGuiDataTurbo.name;
            this.createShare.copyFrom.share.name = this.smartGuiDataTurbo.name;
        }
        this.switch = this.shareSwitch;
        // 如果被赋值关闭，要手动触发一下changhe事件，避免没有disable form
        if (!this.switch) {
            // 在下一个流程中去禁用，暂无更好方法 待优化
            setTimeout(() => {
                this.onSwitchChange(false);
            });
        }
        this.initVm();
        this.setPermissionsNumStr();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.vstoreId && this.vstoreId) {
            this.shareRest = this.service.getBatchRest(this.vstoreId);
            this.createShare.copyFrom.permissions = null;
            this.createShare.copyFrom.permissionNumber = 0;
        }
        if (changes.fsName) {
            this.changesharePathAndName(this.fsName);
        }
    }

    initVm(): void {
        this.vm.addControl('DataTurbocreateStyle', new FormControl(this.createStyle, []));
        this.vm.addControl('DataTurbocopyFromSelect', new FormControl(null, [Validators.required]));
        this.vm.addControl('DataTurbocreateShare', new FormControl(this.createShare, []));

        this.errorMessage = {
            DataTurbocopyFromSelect: {
                required: this.i18n.error.copyFromRequired,
            },
        };

        if (this.createStyle === CreateStyleE.New) {
            this.vm.get('DataTurbocopyFromSelect').disable();
        }
    }

    // 共享开关被切换的时候，需要disable
    onSwitchChange($event): void {
        if ($event) {
            this.vm.get('DataTurbocreateShare').enable();
            this.vm.get('DataTurbocreateStyle').enable();
            if (this.createStyle === CreateStyleE.CopyFrom) {
                this.vm.get('DataTurbocopyFromSelect').enable();
            }
            // 下一个周期去渲染下拉
            setTimeout(() => {
                this.shareSelect.setFixedOption(this.selectShareOpt);
            });
        } else {
            this.vm.get('DataTurbocreateShare').disable();
            this.vm.get('DataTurbocreateStyle').disable();
            if (this.createStyle === CreateStyleE.CopyFrom) {
                this.vm.get('DataTurbocopyFromSelect').disable();
            }
        }
    }

    onCopyShareChange($event): void {
        this.selectShareOpt = $event.option;
        // 未选中时
        if (!this.selectShareOpt) {
            // 用异步规避时序问题导致红色错误清除不掉
            Promise.resolve().then(() => {
                (this.vm.get('DataTurbocopyFromSelect') as FormControl).clearValidate();
            });
        } else {
            // 有选中项时
            // 1、查询并更新这个共享下的client数量
            this.updatePermissions(this.selectShareOpt.data);
            // 2、更新copy的share的其他字段
            this.createShare.copyFrom.share.description = this.selectShareOpt.data.description;
            // 3、更新copy的share的ID和vstoreId，是为了查询copyFrom的数据
            this.createShare.copyFrom.share.ID = this.selectShareOpt.data.ID;
            this.createShare.copyFrom.share.vstoreId = this.selectShareOpt.data.vstoreId;
            // 4、初始化createShare下的copyfrom中的permissions数据
            this.createShare.copyFrom.permissions = null;
        }
    }

    updatePermissions(dataTurbo: DataTurboShare): void {
        this.service.getUserBatch(dataTurbo.vstoreId, dataTurbo.ID).subscribe((data) => {
            this.createShare.copyFrom.permissions = data;
            this.setPermissionsNumStr();
        });
    }

    onCreateStyleChange(value: CreateStyleE): void {
        this.createStyle = value;
        const copyFromSelectCtl = this.vm.get('DataTurbocopyFromSelect');
        if (value === CreateStyleE.New) {
            copyFromSelectCtl.disable();
        }
        if (value === CreateStyleE.CopyFrom) {
            copyFromSelectCtl.enable();
            this.vm.value.DataTurbocreateStyle = CreateStyleE.CopyFrom;
        }
        this.setPermissionsNumStr();
    }

    setPermissionsNumStr(): void {
        const style = this.vm.value.DataTurbocreateStyle;

        this.permissionsNumStr = this.i18n.tip.permisssions(
            this.createShare[style].permissions ?
                this.createShare[style].permissions.length : this.createShare[style].permissionNumber,
        );
    }

    getShareName(fsName: string): string {
        if (!fsName) {
            return '';
        }
        return this.str.getShareName(fsName, this.timestamp);
    }

    // 文件系统名改变的时候，自动组装path和共享名称
    changesharePathAndName(fsName: string): void {
        const path = fsName ? '/'.concat(fsName.concat('/')) : UNPLACEHOLDER;
        this.createShare.new.share.sharePath = path;
        this.createShare.copyFrom.share.sharePath = path;
    }

    modifyShare(): void {
        const style: CreateStyleE.CopyFrom | CreateStyleE.New = this.vm.value.DataTurbocreateStyle;
        // 复用的 DataTurbo的创建页面，并且要传入新的title

        this.doAction(CreateDataTurboShareComponent, {
            copyFromStyle: style,
            copyFromShare: this.createShare[style].share,
            copyFromPermissions: this.createShare[style].permissions,
            entry: this.entry,
            title: this.i18n.title.modifyShare,
            vstoreName: this.vstoreName,
            vstoreId: this.vstoreId,
            supportSmartGui: this.supportSmartGui,
            fileSystem: { name: this.fsName ? this.fsName : UNPLACEHOLDER },
            onClose: ({ data }) => {
                this.createShare[style].permissions = data.permissions;
                this.createShare[style].share.description = data.description;
                this.setPermissionsNumStr();
            },
        });
    }
}
